// Must be evaluated first: registers the unhandledrejection handler before the
// ethora import graph (pulled in via ./App) runs its top-level Firebase Cloud
// Messaging init.
import "./suppress-warnings";

import React from "react";
import { createRoot } from "react-dom/client";

import * as Sentry from "@sentry/react";

import App from "./App";
// Import the internationalization configuration.
import { i18nInitPromise } from "./i18n";
import { FCM_UNSUPPORTED_BROWSER_CODE, isFcmUnsupportedError } from "./utils/fcm-error";
import { isSentryCloudReportingEnabled } from "./utils/sentry-config";

const enableSpotlight = import.meta.env.VITE_SENTRY_SPOTLIGHT === "true";
const cloudReportingEnabled = isSentryCloudReportingEnabled(import.meta.env.VITE_APP_ENV);

Sentry.init({
  // No DSN on non-deployed environments (localhost/e2e) — the SDK has no cloud
  // transport, so nothing reaches Sentry. Spotlight still forwards events to the
  // local sidecar when enabled via `npm run dev:spotlight`.
  dsn: cloudReportingEnabled
    ? "https://739ea51b3e98e0b0d39915e5c4359c78@o1280981.ingest.us.sentry.io/4510952696643584"
    : undefined,
  sendDefaultPii: true,
  release: `medly-practitioner-dashboard@${__APP_VERSION__}+${__GIT_COMMIT__}`,
  environment: import.meta.env.VITE_APP_ENV || "no-environment",
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(enableSpotlight ? [Sentry.spotlightBrowserIntegration()] : []),
  ],
  tracesSampleRate: 1.0,
  spotlight: enableSpotlight,
  // Drop the benign @ethora/chat-component FCM `messaging/unsupported-browser`
  // rejection (Mobile Safari/iOS, non-secure origins) — captured by Sentry's
  // unhandledrejection instrumentation, which a browser-side preventDefault
  // cannot stop. See ./utils/fcm-error. `ignoreErrors` matches the serialized
  // message as a backstop; `beforeSend` matches the raw error's `.code` in case
  // a capture path synthesizes the event without preserving the message text.
  ignoreErrors: [new RegExp(FCM_UNSUPPORTED_BROWSER_CODE.replace("/", "\\/"))],
  beforeSend(event, hint) {
    if (isFcmUnsupportedError(hint?.originalException)) {
      return null;
    }
    return event;
  },
});

const container = document.getElementById("root");
const root = createRoot(container as HTMLElement);

// Conditionally initialize MSW in development only
async function initializeApp() {
  // Wait for i18n translations to load before rendering
  await i18nInitPromise;

  // Only import and initialize MSW in development mode
  // This ensures MSW is completely eliminated from production builds
  if (import.meta.env.DEV) {
    const { initMocks } = await import("./mocks");
    await initMocks();
  }

  root.render(
    <React.StrictMode>
      <React.Suspense fallback="loading">
        <App />
      </React.Suspense>
    </React.StrictMode>
  );
}

initializeApp();
